import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { upgradePlan, getBillingHistory, getInvoices, updateSubscriptionPaymentMethod } from '../../../../apiLayer/dashBoardAPIs/vsCodeDashboardAPIs';
import { getWhopInvoices, getWhopManageUrl, upgradeWhopPlan } from '../../../../apiLayer/dashBoardAPIs/whopAPIs';
import { fetchPaymentProviders, resolvePlanId } from '../../../../utils/paymentProvider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../ui/Card';
import { Button } from '../../../ui/Button';
import { Badge } from '../../../ui/Badge';
import { Skeleton } from '../../../ui/Skeleton';
import {
  CreditCard,
  FileText,
  Download,
  Calendar,
  ExternalLink,
  Loader2,
  ArrowUpRight,
  Sparkles,
  Check,
  AlertCircle
} from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { PRICE_IDS, stripeMode } from '../../../../utils/stripePriceIds';

const FixedSubscriptionBilling = ({ userInfo, subscriptions }) => {
  const navigate = useNavigate();
  const [billingHistory, setBillingHistory] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeBillingCycle, setUpgradeBillingCycle] = useState('monthly');

  // Lifetime owners have no subscription object at all — users_plan_name (from
  // /payment/get_user_info, merged into userInfo by the dashboard hook) is the
  // source of truth. Without this they'd render as free-trial users below.
  const isLifetime = userInfo?.users_plan_name === 'lifetime';

  // Get subscription details from unified payment data (same pattern as Settings.js)
  const getSubscriptionDetails = () => {
    if (isLifetime) {
      return {
        plan_name: 'Lifetime',
        price: 'Paid once',
        billing_cycle: 'N/A',
        status: 'lifetime',
        next_billing_date: 'Never',
        cancel_at_period_end: false,
        cancellation_effective_date: null
      };
    }

    if (!subscriptions?.active_subscription?.plans?.length) {
      return {
        plan_name: 'Free Plan',
        price: '$0.00',
        billing_cycle: 'N/A',
        status: 'inactive',
        next_billing_date: 'N/A',
        cancel_at_period_end: false,
        cancellation_effective_date: null
      };
    }

    const plan = subscriptions.active_subscription.plans[0];
    const sub = subscriptions.active_subscription;

    let status;
    if (sub.status === 'active' && sub.cancel_at_period_end) {
      status = 'cancelling';
    } else if (sub.status === 'active') {
      status = 'active';
    } else if (sub.status === 'canceled' && subscriptions.is_subscriber) {
      status = 'cancelled';
    } else {
      status = 'inactive';
    }

    return {
      plan_name: plan.plan_name,
      // Whop unified data may not carry an amount/interval — guard against null.
      price: plan.amount != null ? `$${(plan.amount / 100).toFixed(2)}` : '—',
      billing_cycle: plan.interval || '—',
      status,
      next_billing_date: subscriptions.next_billing_date
        ? new Date(subscriptions.next_billing_date * 1000).toLocaleDateString()
        : 'N/A',
      cancel_at_period_end: sub.cancel_at_period_end,
      cancellation_effective_date: subscriptions.cancellation_effective_date
    };
  };

  const subDetails = getSubscriptionDetails();
  const isSubscriber = subscriptions?.is_subscriber;
  const provider = subscriptions?.provider || 'stripe';
  const isDegraded = !!subscriptions?.degraded_mode;
  const resubscribeProvider = subscriptions?.prompt_resubscribe_provider;
  const planNameLower = subDetails.plan_name?.toLowerCase() || '';
  const isPro = planNameLower.includes('pro') && !planNameLower.includes('pro+') && !planNameLower.includes('pro plus');

  // Fetch billing history and invoices
  const fetchBillingHistory = useCallback(async () => {
    // Whop has no separate billing-history endpoint — its payments cover it
    // (loaded via fetchInvoices below).
    if (provider === 'whop') {
      setBillingHistory(null);
      setBillingLoading(false);
      return;
    }
    try {
      setBillingLoading(true);
      const data = await getBillingHistory();
      setBillingHistory(data);
    } catch (err) {
      console.error('Error fetching billing history:', err);
    } finally {
      setBillingLoading(false);
    }
  }, [provider]);

  const fetchInvoices = useCallback(async () => {
    try {
      setInvoicesLoading(true);
      const data = provider === 'whop' ? await getWhopInvoices() : await getInvoices();
      setInvoices(data);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    fetchBillingHistory();
    fetchInvoices();
  }, [fetchBillingHistory, fetchInvoices]);

  const handleUpgrade = async () => {
    // Whop upgrade: auto-charge the saved card the prorated difference (no
    // checkout). On success the backend's webhook switches the tier + cancels the
    // old membership; if there's no saved card / the charge declines, it returns a
    // checkout URL to fall back to. (NOT the Stripe /upgrade_plan flow.)
    if (provider === 'whop') {
      try {
        setUpgradeLoading(true);
        const info = await fetchPaymentProviders();
        const planId = resolvePlanId('whop', 'Pro+', upgradeBillingCycle, info);
        if (!planId) { alert('This plan is not available on the current provider yet.'); return; }
        const email = userInfo?.email || localStorage.getItem('user_email');
        const userId = localStorage.getItem('api_key');
        const result = await upgradeWhopPlan({ plan_id: planId, email, user_id: userId });
        if (result?.success) {
          alert('Upgrade successful! Your plan has been updated.');
          setShowUpgradeModal(false);
          window.location.reload();
          return;
        }
        const url = result?.checkout_url || result?.url;
        if (result?.requires_checkout && url) { window.location.href = url; return; }
        alert(result?.message || 'Could not complete the upgrade. Please try again.');
      } catch (e) {
        console.error('Whop upgrade error:', e);
        alert('Failed to upgrade plan. Please try again.');
      } finally {
        setUpgradeLoading(false);
      }
      return;
    }

    const priceId = PRICE_IDS[stripeMode]['Pro+']?.[upgradeBillingCycle];
    if (!priceId) return;

    try {
      setUpgradeLoading(true);
      const result = await upgradePlan(priceId);
      if (result?.fallback_update_payment_method_link) {
        window.location.href = result.fallback_update_payment_method_link;
        return;
      }
      if (result?.requires_payment_update) {
        // Declined with no chargeable invoice (e.g. no card on file) — open the
        // no-charge "update card" checkout; the webhook retries the upgrade after.
        try {
          const successUrl = `${window.location.origin}/pricing?card_updated=true`;
          const cancelUrl = `${window.location.origin}/pricing`;
          const r = await updateSubscriptionPaymentMethod(successUrl, cancelUrl);
          if (r?.checkout_url) {
            window.location.href = r.checkout_url;
            return;
          }
        } catch (e) {
          console.error('update payment method fallback failed:', e);
        }
        alert(result.message || 'Please update your payment method to upgrade.');
        return;
      }
      if (result?.success === false) {
        alert(result.message || 'Could not complete the upgrade. Please try again.');
        return;
      }
      if (result.success || result.message) {
        alert(result.message || 'Upgrade successful! Your plan has been updated.');
        setShowUpgradeModal(false);
        window.location.reload();
      }
    } catch (err) {
      console.error('Error upgrading plan:', err);
      alert(err.response?.data?.detail || 'Failed to upgrade plan. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    // Whop manages payment methods on its own hosted billing portal — redirect to
    // the membership's manage_url (Whop's equivalent of the Stripe billing portal).
    // Whop has no API to create a setup session or push a card-update email.
    if (provider === 'whop') {
      try {
        setUpgradeLoading(true);
        const result = await getWhopManageUrl();
        if (result?.url) {
          window.open(result.url, '_blank', 'noopener');
        } else {
          alert('Could not open the Whop billing page. Please try again shortly.');
        }
      } catch (err) {
        console.error('Error opening Whop billing portal:', err);
        alert('Failed to open the Whop billing page. Please try again.');
      } finally {
        setUpgradeLoading(false);
      }
      return;
    }
    try {
      setUpgradeLoading(true);
      const successUrl = `${window.location.origin}/vscode/dashboard?card_updated=true`;
      const cancelUrl = `${window.location.origin}/vscode/dashboard`;
      const result = await updateSubscriptionPaymentMethod(successUrl, cancelUrl);
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      console.error('Error updating payment method:', err);
      alert('Failed to update payment method. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'lifetime':
        return <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">Lifetime</Badge>;
      case 'active':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">Active</Badge>;
      case 'cancelling':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">Cancelling</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">Cancelled</Badge>;
      default:
        return <Badge variant="outline" className="bg-gray-500/10 text-gray-400 border-gray-500/30">Inactive</Badge>;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Billing</h1>
        <p className="text-sm text-muted-foreground">Manage your subscription and billing</p>
      </div>

      <div className="space-y-6 max-w-5xl">
        {/* Degraded-mode banner — shown when the primary provider (Stripe) is
            temporarily unavailable and/or the user should resubscribe via Whop. */}
        {(isDegraded || resubscribeProvider) && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10">
            <AlertCircle className="w-5 h-5 text-yellow-300 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-200">
                {resubscribeProvider && !isSubscriber
                  ? 'Your subscription has expired'
                  : 'Billing temporarily unavailable'}
              </p>
              <p className="text-sm text-yellow-100/80 mt-1">
                {resubscribeProvider && !isSubscriber
                  ? `Resubscribe via ${resubscribeProvider} to restore full access.`
                  : 'We’re having trouble reaching our billing provider. Your access is unaffected; some billing details may be temporarily out of date.'}
              </p>
              {resubscribeProvider && !isSubscriber && (
                <Button className="mt-3" onClick={() => navigate('/pricing')}>
                  Resubscribe
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Current Subscription */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <CardTitle>Current Subscription</CardTitle>
            </div>
            <CardDescription>Your plan and subscription details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!subscriptions ? (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Skeleton className="h-24" />
                  <Skeleton className="h-24" />
                  <Skeleton className="h-24" />
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between p-4 bg-white/5 rounded-lg border border-white/5">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-3 rounded-lg",
                      subDetails.status === 'lifetime' ? "bg-amber-500/10" :
                      subDetails.status === 'active' ? "bg-green-500/10" :
                      subDetails.status === 'cancelling' ? "bg-yellow-500/10" : "bg-white/5"
                    )}>
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-white">
                          {subDetails.plan_name}
                        </h3>
                        {getStatusBadge(subDetails.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {isLifetime
                          ? 'Paid once — full platform access, forever. Nothing to renew or cancel.'
                          : isSubscriber
                            ? subDetails.status === 'cancelling'
                              ? `Access until ${subDetails.cancellation_effective_date ? new Date(subDetails.cancellation_effective_date * 1000).toLocaleDateString() : subDetails.next_billing_date}`
                              : 'Full platform access with all features'
                            : 'Subscribe to unlock all features'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">{subDetails.price}</span>
                    {subDetails.billing_cycle && subDetails.billing_cycle !== 'N/A' && (
                      <span className="text-sm text-muted-foreground">/{subDetails.billing_cycle}</span>
                    )}
                  </div>
                </div>

                {/* Subscription detail cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                      <CreditCard className="w-4 h-4" />
                      Plan
                    </div>
                    <div className="text-xl font-bold text-white">
                      {subDetails.plan_name}
                    </div>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                      <Calendar className="w-4 h-4" />
                      Billing Cycle
                    </div>
                    <div className="text-xl font-bold text-white capitalize">
                      {isLifetime ? 'One-time' : (subDetails.billing_cycle || 'N/A')}
                    </div>
                  </div>

                  {subDetails.status === 'cancelling' || subDetails.status === 'cancelled' ? (
                    <div className="p-4 bg-yellow-500/5 rounded-lg border border-yellow-500/10">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                        <Calendar className="w-4 h-4" />
                        Access Until
                      </div>
                      <div className="text-lg font-bold text-white">
                        {subDetails.cancellation_effective_date
                          ? new Date(subDetails.cancellation_effective_date * 1000).toLocaleDateString()
                          : subDetails.next_billing_date}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                        <Calendar className="w-4 h-4" />
                        Next Billing Date
                      </div>
                      <div className="text-lg font-bold text-white">
                        {subDetails.next_billing_date}
                      </div>
                    </div>
                  )}
                </div>

                {/* Update payment method (for active subscribers) */}
                {isSubscriber && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={handleUpdatePaymentMethod}
                      disabled={upgradeLoading}
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      {upgradeLoading ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-1" />
                      )}
                      Update payment method
                    </Button>
                  </div>
                )}

                {/* Subscribe CTA for free/unsubscribed users (never for lifetime owners) */}
                {!isSubscriber && !isLifetime && (
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-white mb-1">Upgrade Your Plan</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Get unlimited access to all features, premium models, and priority support.
                      </p>
                      <Button
                        onClick={() => navigate('/pricing')}
                        className="bg-gradient-to-r from-[#E005FF] to-[#A73CFF] hover:opacity-90 text-white border-0"
                      >
                        View Plans <ArrowUpRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Upgrade to Pro+ (only if current plan is Pro) */}
                {isPro && subDetails.status === 'active' && (
                  <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20 flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-white mb-1">Upgrade to Pro+</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Get 30x premium requests, Claude Opus 4.5, unlimited MCP, fastest responses, and 24/7 support.
                      </p>
                      <Button
                        onClick={() => setShowUpgradeModal(true)}
                        className="bg-gradient-to-r from-purple-500 to-violet-500 hover:opacity-90 text-white border-0"
                      >
                        Upgrade to Pro+ <ArrowUpRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Billing History — Stripe only. Whop has no separate billing-history
            endpoint; its payments are shown in the Invoices section below. */}
        {provider !== 'whop' && (
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <CardTitle>Billing History</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {billingLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-white/5">
                    <tr>
                      <th className="px-4 py-3 rounded-l-lg">Date</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 rounded-r-lg text-right">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory?.data?.length > 0 ? (
                      billingHistory.data.map((payment, idx) => (
                        <tr key={payment.payment_id || idx} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3 text-white">{formatDate(payment.created)}</td>
                          <td className="px-4 py-3 text-white font-medium">${((payment.amount || 0) / 100).toFixed(2)}</td>
                          <td className="px-4 py-3 text-muted-foreground capitalize">{payment.description || payment.subscription_type || 'Subscription'}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={cn(
                              payment.status === 'succeeded' || payment.status === 'paid'
                                ? "bg-green-500/10 text-green-500 border-green-500/30"
                                : "bg-yellow-500/10 text-yellow-500 border-yellow-500/30"
                            )}>
                              {payment.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {payment.receipt_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(payment.receipt_url, '_blank')}
                                className="h-8 hover:bg-white/10"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                          No billing history found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Invoices */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                <CardTitle>Invoices</CardTitle>
              </div>
              {invoices?.total_invoices > 0 && (
                <span className="text-sm text-muted-foreground">
                  {invoices.total_invoices} invoice{invoices.total_invoices > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {invoicesLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-white/5">
                    <tr>
                      <th className="px-4 py-3 rounded-l-lg">Invoice #</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 rounded-r-lg text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices?.data?.length > 0 ? (
                      invoices.data.map((invoice) => {
                        const downloadUrl = invoice.invoice_pdf || invoice.pdf_url || invoice.download_url ||
                                           invoice.pdf || invoice.url || invoice.hosted_invoice_url;

                        return (
                          <tr key={invoice.invoice_id || invoice.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                            <td className="px-4 py-3 text-white font-mono text-xs">{invoice.invoice_number || invoice.number || invoice.id}</td>
                            <td className="px-4 py-3 text-white">{formatDate(invoice.created_at || invoice.created || invoice.date)}</td>
                            <td className="px-4 py-3 text-white font-medium">${((invoice.amount_due || invoice.total || 0) / 100).toFixed(2)}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className={cn(
                                invoice.status === 'paid'
                                  ? "bg-green-500/10 text-green-500 border-green-500/30"
                                  : "bg-yellow-500/10 text-yellow-500 border-yellow-500/30"
                              )}>
                                {invoice.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                {invoice.hosted_invoice_url && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                                    className="h-8 hover:bg-white/10"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-1" /> View
                                  </Button>
                                )}
                                {downloadUrl && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(downloadUrl, '_blank')}
                                    className="h-8 bg-primary/10 hover:bg-primary/20 border-primary/30"
                                  >
                                    <Download className="w-4 h-4 mr-1" /> Download
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                          No invoices found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-[#121219] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-500/10">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Upgrade to Pro+</h3>
                  <p className="text-sm text-muted-foreground">Choose your billing cycle</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setUpgradeBillingCycle('monthly')}
                  className={cn(
                    "p-4 rounded-lg border text-center transition-all",
                    upgradeBillingCycle === 'monthly'
                      ? "border-purple-500 bg-purple-500/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  )}
                >
                  <div className="text-lg font-bold text-white">$30</div>
                  <div className="text-sm text-muted-foreground">/month</div>
                </button>
                <button
                  onClick={() => setUpgradeBillingCycle('yearly')}
                  className={cn(
                    "p-4 rounded-lg border text-center transition-all relative",
                    upgradeBillingCycle === 'yearly'
                      ? "border-purple-500 bg-purple-500/10"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  )}
                >
                  <Badge className="absolute -top-2 -right-2 bg-green-500 text-white border-0 text-[10px]">
                    Save 25%
                  </Badge>
                  <div className="text-lg font-bold text-white">$270</div>
                  <div className="text-sm text-muted-foreground">/year</div>
                </button>
              </div>

              <div className="space-y-2 pt-2">
                <h4 className="text-sm font-medium text-white">What's included in Pro+:</h4>
                <ul className="space-y-1.5">
                  {[
                    '30x premium requests',
                    'Claude Opus 4.7 access',
                    'Unlimited MCP servers',
                    'Fastest response times',
                    '24/7 priority support'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-purple-400 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-purple-500 to-violet-500 hover:opacity-90 text-white border-0"
                  onClick={handleUpgrade}
                  disabled={upgradeLoading}
                >
                  {upgradeLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Upgrading...</>
                  ) : (
                    `Upgrade - $${upgradeBillingCycle === 'monthly' ? '30/mo' : '270/yr'}`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FixedSubscriptionBilling;
